const SerialPortCommunicationHandlerProvider = require("./Devices/SerialPortCommunicationHandlerProvider");
const WebSerialDisplayControl = require("./WebSerialDisplayControl");
const WebSerialForgetHandler = require("./WebSerialForgetHandler");

const Logger = require("../Logger");

const INVALID_VOLUME_CONTROL_CONFIGURATION = "E000000112";
const VOLUME_CONTROL_COMMAND_EXECUTION_ERROR = "E000000113";

const ALERT_VOLUME_STATE = "alert";
const DEFAULT_VOLUME_STATE = "default";

const SECONDS = 1000;
const CURRENT_VOLUME_READ_TIMEOUT = 5 * SECONDS;
const MAX_VOLUME_READ_ATTEMPTS = 4;

const service = {
  serialPortCommunicationHandler: SerialPortCommunicationHandlerProvider.getSerialPortCommunicationHandler(),
  volumeControlSupported: false,
  alertVolumePercentage: null,
  currentVolumePercentage: null,
  pendingVolumeState: null,
  lastSuccessfulVolumeState: null
};

service.isVolumeControlEnabled = () => service.volumeControlSupported;

service.getSerialPortCommunicationHandler = () => service.serialPortCommunicationHandler;

const performSerialConnectionSettingsValidation = () => {
  try {
    WebSerialDisplayControl.checkConnectionSettingsMatchHandlerExpectations( service.serialPortCommunicationHandler );

    return true;
  } catch( error ) {
    Logger.viewerError(
      INVALID_VOLUME_CONTROL_CONFIGURATION,
      `Existing connection settings do not match ${ service.serialPortCommunicationHandler.getType() } connection settings`,
      { error: error && ( error.stack || error.message ) }
    );
  
    service.stop();
   
    return false;
  }
}

const createVolumeListener = command => {
  let resolve, reject;
  let timeout = null;

  const promise = new Promise((resolve_, reject_) => {
    resolve = resolve_;
    reject = reject_;
  });

  const createVolumeListenerTimeout = currentAttempt => {
    return setTimeout( () => {
      if( currentAttempt <= MAX_VOLUME_READ_ATTEMPTS ) {
        Logger.viewerError(
          VOLUME_CONTROL_COMMAND_EXECUTION_ERROR,
          "Error reading current volume percentage, retrying"
        );

        sendCommand( command )
        .catch( error => {
          Logger.viewerError(
            VOLUME_CONTROL_COMMAND_EXECUTION_ERROR,
            "Error requesting again current volume percentage, we'll retry later",
            { error: error && ( error.stack || error.message ) }
          );
        });

        timeout = createVolumeListenerTimeout( currentAttempt + 1 );
      } else {
        reject( new Error( "Timeout loading current volume percentage" ) );
      }
    }, CURRENT_VOLUME_READ_TIMEOUT );
  }

  timeout = createVolumeListenerTimeout( 1 );

  const stopTimeout = () => {
    if( timeout ) {
      clearTimeout( timeout );
      timeout = null;
    }
  };

  return {
    getVolume: () => promise,
    onVolume: volume => {
      stopTimeout();
      resolve( volume );
    },
    onError: error => {
      stopTimeout();
      reject( error );
    }
  }
};

const sendCommand = command => {
  return WebSerialDisplayControl.sendCommand( command, {errorCode: VOLUME_CONTROL_COMMAND_EXECUTION_ERROR} );
}

const loadCurrentVolumePercentage = () => {
  return Promise.resolve().then(() => {
    const command = service.serialPortCommunicationHandler.getReadVolumeCommand();
    const volumeListener = createVolumeListener(command);
    service.serialPortCommunicationHandler.addVolumeListener( volumeListener );

    return sendCommand( command )
    .then( () => volumeListener.getVolume() )
    .then( volume => {
      if( volume === service.alertVolumePercentage ) {
        Logger.viewerError(
          INVALID_VOLUME_CONTROL_CONFIGURATION,
          "Current volume percentage is the same as alert volume percentage, stopping volume control. User should manually adjust the volume in the TV to the desired default level.",
          { volume }
        );

        service.stop();

        return;
      }
  
      console.warn( "Loaded current volume percentage", volume );
  
      service.currentVolumePercentage = volume;
    })
    .finally( () => {
      service.serialPortCommunicationHandler.removeVolumeListener( volumeListener );
    });
  })
  .catch( error => {
    Logger.viewerError(
      VOLUME_CONTROL_COMMAND_EXECUTION_ERROR,
      "Could not load current volume percentage, stopping volume control",
      { error: error && ( error.stack || error.message ) }
    );

    service.stop();
  });
}

const switchToPendingVolumeState = () => {
  // Switch to alert volume immediately if the display is already showing priority content.
  if( service.pendingVolumeState === ALERT_VOLUME_STATE ) {
    service.pendingVolumeState = null;

    return service.setVolumeLevelToAlert();
  } else {
    service.pendingVolumeState = null;

    service.lastSuccessfulVolumeState = DEFAULT_VOLUME_STATE;
  }

  return Promise.resolve();
};

const configureSerialPortReader = () => {
  if( WebSerialDisplayControl.isSerialPortReaderConfigured() ) {
    console.log( "Serial port reader already configured, returning" );

    return Promise.resolve();
  }

  return WebSerialDisplayControl.configureSerialPortReader( service.serialPortCommunicationHandler )
  .then( configured => {
    console.log( "Serial port reader configured", configured );
    
    return loadCurrentVolumePercentage();
  })
  .then( switchToPendingVolumeState )
  .catch( error => {
    Logger.viewerError(
      INVALID_VOLUME_CONTROL_CONFIGURATION,
      "Could not configure serial port reader, stopping volume control",
      { error: error && ( error.stack || error.message ) }
    );

    service.stop();
  });
}

const CONNECTION_READY_LISTENER = {
  onSerialConnectionReady: () => {
    // No need to wait for the promise to resolve here; it's handled in the configureSerialPortReader function
    configureSerialPortReader();

    // No longer necessary to listen for serial connection ready event
    unregisterSerialConnectionReadyListener();
  }
};

const registerSerialConnectionReadyListener = () => {
  WebSerialDisplayControl.addSerialConnectionReadyListener( CONNECTION_READY_LISTENER );
};

const unregisterSerialConnectionReadyListener = () => {
  WebSerialDisplayControl.removeSerialConnectionReadyListener( CONNECTION_READY_LISTENER );
};

const configureVolumeControlOnTopOfDisplayControlConfiguration = () => {
  if( !WebSerialDisplayControl.isSerialConnectionReady() ) {
    console.warn( "Connection not ready; volume control won't configure until it's ready" );

    registerSerialConnectionReadyListener();
    return;
  }

  return configureSerialPortReader();
};

const configureSpecificDeviceConnectionAndSerialPortReader = () => {
  return WebSerialDisplayControl.configureSpecificDeviceConnection( service.serialPortCommunicationHandler )
  .then( () => configureVolumeControlOnTopOfDisplayControlConfiguration() );
};

service.configure = ( volumeControlSupported, alertVolumePercentage ) => {
  return Promise.resolve().then(() => {
    console.log( "VolumeControlHandler.configure", volumeControlSupported, alertVolumePercentage );
    service.volumeControlSupported = volumeControlSupported;
    service.alertVolumePercentage = alertVolumePercentage;

    if( !volumeControlSupported ) {
      WebSerialForgetHandler.setVolumeControlEnabled( false );

      return service.stop();
    }

    WebSerialForgetHandler.setVolumeControlEnabled( true );

    if( WebSerialDisplayControl.isConfigured() ) {
      console.warn( "Serial connection already configured; will attempt to reuse it");

      if( service.currentVolumePercentage !== null ) {
        return;
      }

      // Validate that existing connection setting matches device expected connection settings; if not, send an error and disable volume control
      if( !performSerialConnectionSettingsValidation() ) {
        return;
      }
    
      return configureVolumeControlOnTopOfDisplayControlConfiguration();
    }

    // Display control has not been configured; so configure the serial connection using specific device settings.
    console.warn( `Configuring serial connection using ${ service.serialPortCommunicationHandler.getType() } settings` );
    return configureSpecificDeviceConnectionAndSerialPortReader();
  }).catch((error) => {
    service.stop();

    Logger.viewerError(
      INVALID_VOLUME_CONTROL_CONFIGURATION,
      "Could not configure volume control",
      {
        volumeControlSupported,
        volumePercentage: alertVolumePercentage,
        error: error && ( error.stack || error.message )
      }
    );
  });
};

const setVolumeLevel = ( volumeLevel, commandType ) => {
  if( !service.volumeControlSupported || service.alertVolumePercentage === null ) {
    service.pendingVolumeState = commandType;

    return Promise.resolve();
  }

  if( service.currentVolumePercentage === null || !WebSerialDisplayControl.isSerialConnectionReady() ) {
    service.pendingVolumeState = commandType;

    console.warn( "Serial port connection not ready; volume control won't set volume level until it's ready" );

    return Promise.resolve();
  }

  if( service.lastSuccessfulVolumeState === commandType ) {
    console.warn( `Volume control already in the desired state (${ commandType }); skipping` );

    return Promise.resolve();
  }

  let volumeLevelCommand = null;

  try {
    volumeLevelCommand = service.serialPortCommunicationHandler.getVolumeCommandFor( volumeLevel );
  } catch( error ) {
    Logger.viewerError(
      VOLUME_CONTROL_COMMAND_EXECUTION_ERROR,
      "Could not get volume level command",
      { volumeLevel, commandType, error: error && ( error.stack || error.message ) }
    );

    // Error already logged
    return Promise.resolve();
  }

  return sendCommand( volumeLevelCommand )
  .then( success => {
    if( success ) {
      service.lastSuccessfulVolumeState = commandType;
    }
  });
}

service.setVolumeLevelToAlert = () => {
  return setVolumeLevel( service.alertVolumePercentage, ALERT_VOLUME_STATE );
};

service.setVolumeLevelToDefault = () => {
  return setVolumeLevel( service.currentVolumePercentage, DEFAULT_VOLUME_STATE );
};

const cleanConfigurationFields = () => {
  service.volumeControlSupported = false;
  service.alertVolumePercentage = null;
  service.currentVolumePercentage = null;
  service.lastSuccessfulVolumeState = null;
};

service.stop = () => {
  console.warn( "Disabling volume control" );

  cleanConfigurationFields();
  unregisterSerialConnectionReadyListener();

  // If no power commands are available, stop any existing connection.
  if( !WebSerialDisplayControl.powerCommandsAvailable() ) {
    console.warn( "Stopping serial connection completely" );
    service.serialPortCommunicationHandler.clean();
    WebSerialDisplayControl.stop();
  }
};

// For test purposes only
service.clear = () => {
  cleanConfigurationFields();

  // This is usually not cleared until viewer restarts.
  service.pendingVolumeState = null;
};

module.exports = service;
